// Dependencies
import React, { Fragment, FunctionComponent, useEffect } from 'react'

// Types
import { NavbarButtonProps } from './navbar-button.types'

// Styles
import {
  Container,
  ContentWrapper,
  Label,
  Loader,
  LoaderWrapper
} from './navbar-button.styles'

// Components

// Utils
import { ternary } from 'utils/ternary'
import { ConditionallyRender } from 'component-library/conditionally-render'

export const NavbarButton: FunctionComponent<NavbarButtonProps> = ({
  isLoading,
  isDisabled,
  isFullyAdaptative,
  borderType = 'large',
  variant = 'primary',
  variantType = 'default',
  label,
  iconElement,
  handleClick,
  ...buttonDefaultProps
}) => {
  const backgroundColor = ternary([
    [variant === 'primary' && variantType !== 'link', '#C11636'],
    [variant === 'secondary' && variantType !== 'link', '#FDF4F5'],
    [variant === 'white' && variantType !== 'link', '#fff'],
    [variant === 'info' && variantType !== 'link', '#F7FAFC'],
    [variantType === 'link', 'transparent']
  ]) as string
  const textColor = ternary([
    [variant === 'primary' && variantType !== 'link', '#fff'],
    [variant === 'secondary' && variantType !== 'link', '#DF1D3D'],
    [variant === 'white' && variantType !== 'link', '#C11636'],
    [variant === 'info' && variantType !== 'link', '#53A4C6'],
    [variantType === 'link' && variant === 'primary', '#DF1D3D'],
    [variantType === 'link' && variant === 'info', '#3E68D2']
  ]) as string
  const borderColor = ternary([
    [variant === 'secondary' && variantType !== 'link', '#C11636'],
    [variant === 'info' && variantType !== 'link', '#53A4C6']
  ]) as string
  const buttonSize = ternary([
    [!label && !!iconElement, 'small'],
    [!!label, 'large']
  ]) as 'large' | 'small'

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.key === 'Enter') {
        event.preventDefault()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <Container
      className="pdg-navbar-button"
      size={buttonSize}
      backgroundColor={backgroundColor}
      hasSpacing={variantType !== 'link'}
      isFullyAdaptative={!!isFullyAdaptative}
      disabled={isDisabled || isLoading}
      borderType={borderType}
      borderColor={borderColor}
      hasCursorPointer={!!handleClick || buttonDefaultProps.type === 'submit'}
      {...(!isDisabled &&
        !isLoading && { onClick: (ev) => !!handleClick && handleClick(ev) })}
      {...buttonDefaultProps}
    >
      <ConditionallyRender
        shouldRender={!isLoading}
        content={
          <ContentWrapper>
            <ConditionallyRender
              shouldRender={!!label}
              content={
                <Label
                  textColor={textColor}
                  hasUnderline={variantType === 'link'}
                >
                  {label}
                </Label>
              }
            />

            <ConditionallyRender
              shouldRender={!!iconElement}
              content={iconElement}
            />
          </ContentWrapper>
        }
      />

      <ConditionallyRender
        shouldRender={!!isLoading}
        content={
          <LoaderWrapper>
            <Loader color={textColor}>
              <div>
                <div />
                <div />
                <div />
                <div />
              </div>
            </Loader>
          </LoaderWrapper>
        }
      />
    </Container>
  )
}
